import { ClipboardModule } from '@angular/cdk/clipboard';
import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
  booleanAttribute,
  forwardRef,
  numberAttribute,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { endsWith, isNil, isString } from 'lodash-es';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import {
  CanUpdateErrorState,
  FormFieldControl,
  mixinErrorState,
} from '@aw/shared/components/form-field';
import { IconModule, SvgIconName } from '@aw/shared/components/icon';
import { LoaderComponent } from '@aw/shared/components/loader';
import { InputColor } from '@aw/shared/constants';
import { NumbersOnlyDirective, RippleDirective } from '@aw/shared/directives';
import { isEmptyString } from '@aw/shared/utilities';

import { inputTypeInputMode } from './constants/input-type-to-input-model.const';
import { InputMode, InputType } from './types/types';

const _InputBase = mixinErrorState(class {});

@Component({
  standalone: true,
  selector: 'aw-input',
  templateUrl: 'input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [
    NgIf,
    NgClass,
    FormsModule,
    RippleDirective,
    NumbersOnlyDirective,
    LoaderComponent,
    IconModule,
    NgxMaskDirective,
    ClipboardModule,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideNgxMask(),
    {
      provide: FormFieldControl,
      useExisting: forwardRef(() => InputComponent),
    },
  ],
})
export class InputComponent
  extends _InputBase
  implements
    DoCheck,
    OnDestroy,
    FormFieldControl<any>,
    ControlValueAccessor,
    CanUpdateErrorState
{
  static nextId = 0;

  @HostBinding('class.aw-input')
  readonly identifierClass = true;

  @Input()
  set value(_value: any) {
    this._value = _value;
    this.stateChanges.next();
  }

  get value(): string {
    return this._value;
  }

  @Input() label?: string;

  @Input() placeholder = '';

  @Input() name = '';

  @Input() autocomplete = 'off';

  @Input() color: InputColor = 'white';

  @Input() icon?: SvgIconName;

  @Input() styleClasses = '';

  @Input({ transform: numberAttribute }) min?: number;

  @Input({ transform: numberAttribute }) max?: number;

  @Input({ transform: booleanAttribute }) disabled = false;

  @Input({ transform: booleanAttribute }) spellcheck = false;

  @Input({ transform: booleanAttribute }) allowNegativeNumbers = false;

  @Input({ transform: booleanAttribute }) loading = false;

  @Input({ transform: booleanAttribute }) readonly = false;

  @Input({ transform: booleanAttribute }) debug = false;

  @Input({ transform: booleanAttribute }) copyable = false;

  @Input({ required: false }) isShowDirhamIcon = false;

  @HostBinding('attr.dropSpecialCharacters')
  @Input()
  dropSpecialCharacters = true;

  @Input()
  set type(_type: InputType) {
    this._type = _type;

    if (_type === 'email' && this.autocomplete !== 'off') {
      this.autocomplete = 'email';
    }
  }

  get type(): InputType {
    return this._type;
  }

  @Input() mask: string | undefined | null;

  @Input()
  set prefix(_prefix: any) {
    this._prefix = this.normalizePrefix(_prefix);
  }

  get prefix(): string {
    return this._prefix;
  }

  @Input()
  set suffix(_suffix: any) {
    this._suffix = this.normalizeSuffix(_suffix);
  }

  get suffix(): string {
    return this._suffix;
  }

  @Input() thousandSeparator = ',';

  get inputMode(): InputMode {
    return inputTypeInputMode[this.type] || 'text';
  }

  get shouldPlaceholderFloat(): boolean {
    return (
      !isEmptyString(this.placeholder) &&
      (this.isFocused || (!isNil(this.value) && !isEmptyString(this.value)))
    );
  }

  @HostBinding('attr.masked')
  get isMasked(): boolean {
    return !!this.mask;
  }

  @Output()
  readonly valueChange = new EventEmitter<any>();

  @Output()
  readonly blured = new EventEmitter<void>();

  onTouched?: () => void;

  onChange?: (value: string) => void;

  readonly inputId = `po-input-${InputComponent.nextId++}`;

  constructor(
    @Optional() @Self() readonly ngControl: NgControl,
    private readonly changeDetector: ChangeDetectorRef,
  ) {
    super();

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  @ViewChild('input', { static: true })
  private readonly input?: ElementRef<HTMLInputElement>;

  private isFocused = false;

  private _value = '';

  private _type: InputType = 'text';

  private _prefix = '';

  private _suffix = '';

  get inputValue(): string | undefined {
    if (this.input) {
      return this.input.nativeElement.value;
    }

    return undefined;
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  onInput(value: string): void {
    if (this.type === 'number') {
      if (isEmptyString(value)) {
        this.value = undefined;
      } else {
        const isFloat = value?.toString().includes('.');

        this.value = isFloat ? parseFloat(value) : parseInt(value, 10);
      }
    } else {
      this.value = value;
    }

    this.emitValue(this.value);
  }

  onFocus(): void {
    this.isFocused = true;
  }

  onBlur(): void {
    this.isFocused = false;

    this.correctValue();

    if (this.onTouched) {
      this.onTouched();
    }

    this.blured.emit();
  }

  writeValue(value: string | number | null): void {
    if (this.type === 'number' && value === 0) {
      this.value = 0;
    } else {
      this.value = value || '';
    }
    this.changeDetector.markForCheck();
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.stateChanges.next();
    this.changeDetector.detectChanges();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  private correctValue(): void {
    if (this.type === 'number' && !isNil(this.value)) {
      const isFloat = this.value.toString().includes('.');

      let parsedValue: number | undefined = isFloat
        ? parseFloat(this.value)
        : parseInt(this.value, 10);

      if (!isNil(this.min) && parsedValue < this.min) {
        parsedValue = this.min;
      }

      if (!isNil(this.max) && parsedValue > this.max) {
        parsedValue = this.max;
      }

      if (isNaN(parsedValue)) {
        parsedValue = undefined;
      }

      this.value = parsedValue;
      this.emitValue(parsedValue);
    }
  }

  private emitValue(value: any): void {
    if (this.onChange) {
      this.onChange(value);
    } else {
      this.valueChange.emit(value);
    }
  }

  private readonly normalizePrefix = (prefix: string): string => {
    if (isString(prefix) && !endsWith(prefix, ' ')) {
      return `${prefix} `;
    }

    return prefix || '';
  };

  private readonly normalizeSuffix = (suffix: string): string => {
    if (isString(suffix) && !endsWith(suffix, ' ')) {
      return ` ${suffix}`;
    }

    return suffix || '';
  };
}
